import { z } from "zod";

/** Content areas the command palette can search across. */
export const searchType = z.enum(["vault", "prompt", "ritual"]);
export type SearchType = z.infer<typeof searchType>;

/** Fields every search hit shares regardless of source. */
const searchResultBase = z.object({
  id: z.string().min(1),
  /** Human deep-link handle; may be null for un-backfilled rows. */
  slug: z.string().nullable(),
  title: z.string(),
  /** A short, plain-text excerpt with the match context. */
  snippet: z.string(),
});

/** A hit from the vault (a note or an external link). */
export const vaultSearchResult = searchResultBase.extend({
  type: z.literal("vault"),
  kind: z.enum(["note", "tool", "read", "reference"]),
});
export type VaultSearchResult = z.infer<typeof vaultSearchResult>;

/** A hit from the prompts library. */
export const promptSearchResult = searchResultBase.extend({
  type: z.literal("prompt"),
  useCase: z.string(),
});
export type PromptSearchResult = z.infer<typeof promptSearchResult>;

/** A hit from the weekly rituals. */
export const ritualSearchResult = searchResultBase.extend({
  type: z.literal("ritual"),
  week: z.number().int(),
});
export type RitualSearchResult = z.infer<typeof ritualSearchResult>;

/** Discriminated union of every kind of search hit. */
export const searchResult = z.discriminatedUnion("type", [
  vaultSearchResult,
  promptSearchResult,
  ritualSearchResult,
]);
export type SearchResult = z.infer<typeof searchResult>;

/** Response envelope for a search request. */
export const searchResponse = z.object({
  query: z.string(),
  items: z.array(searchResult),
  total: z.number().int(),
});
export type SearchResponse = z.infer<typeof searchResponse>;

/** Maximum hits returned per request (kept small for a palette). */
export const DEFAULT_SEARCH_LIMIT = 20;
export const MAX_SEARCH_LIMIT = 50;

/**
 * Query params for `GET /api/search`. `types` is an optional comma-separated
 * list of areas to restrict the search to; omitted means search everything.
 */
export const searchQuery = z.object({
  q: z.string().trim().min(1).max(200),
  types: z
    .string()
    .optional()
    .transform((v) =>
      v
        ? v
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined,
    )
    .pipe(z.array(searchType).nonempty().optional()),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_SEARCH_LIMIT)
    .default(DEFAULT_SEARCH_LIMIT),
});
export type SearchQuery = z.infer<typeof searchQuery>;

/** Params accepted by the client `search` method (all but `q` optional). */
export interface SearchParams {
  types?: SearchType[];
  limit?: number;
}
